<template>
  <div class="flex flex-col h-full flex-shrink-0 rounded-2xl relative z-10">
    <div
      ref="panelContent"
      class="flex flex-col h-full py-2 pr-2 desktop:pr-3 ultra:pr-5 pl-3 desktop:pl-4 ultra:pl-5 gap-2"
    >
      <div>
        <div class="flex justify-between items-center self-stretch">
          <div class="flex items-center space-x-2">
            <img
              :src="userStore.profileImage"
              alt="User Avatar"
              class="rounded-full w-8 h-8"
              @error="onErrorImage"
            />
            <span
              class="text-darkGray font-geist text-base font-medium leading-normal"
              >{{
                userStore.fullName || userStore.firstName || $t('layoutLabels.guest')
              }}</span
            >
          </div>
          <button
            ref="closeButton"
            type="button"
            @click="rightPanelStore.hideRightPanel"
            @keydown.tab="handleTabFromClose"
            class="p-2 rounded-lg cursor-pointer bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deepSky"
            :aria-label="$t('accessibilityLabels.closeMenu')"
            tabindex="0"
          >
            <div
              v-html="closeSvg({ width: '24px', height: '24px' })"
              class="h-full w-auto pointer-events-none"
            ></div>
          </button>
        </div>

        <div class="w-full h-[1px] bg-whiteBlue my-4"></div>
        <div class="flex justify-between items-center text-blueNeutral my-12">
          <span class="text-deepSky font-just text-h2 font-semibold">{{
            $t('layoutLabels.menu')
          }}</span>
        </div>
      </div>

      <nav class="flex-1 space-y-8">
        <button
          type="button"
          v-for="(item, index) in mainRightPanelMenuItems"
          :key="item.id"
          :ref="index === 0 ? 'firstNavButton' : undefined"
          @click="() => handleNavigation(item.path)"
          @keydown.shift.tab.exact="index === 0 ? handleShiftTabFromFirst($event) : null"
          @keydown.tab.exact="index === mainRightPanelMenuItems.length - 1 ? handleTabFromLastNav($event) : null"
          class="flex w-full pr-4 justify-end items-center gap-4 self-stretch text-greyBlue font-pop font-medium cursor-pointer bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deepSky"
          tabindex="0"
        >
          <div class="flex justify-center gap-2">
            <span
              class="text-btnActive font-just text-h3 ultra:text-h2 font-normal text-right flex item-center"
              >{{ item.label }}</span
            >
            <div v-html="item.icon" class="w-5 h-5 pointer-events-none"></div>
          </div>
        </button>
      </nav>

      <div class="space-y-8">
        <button
          ref="logoutButton"
          type="button"
          @click="() => handleLogout()"
          @keydown.tab.exact="handleTabFromLogout"
          class="flex w-full pr-4 pl-0 justify-end items-center gap-4 self-stretch text-greyBlue font-pop font-medium cursor-pointer bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deepSky"
          tabindex="0"
        >
          <span
            class="text-btnActive font-just text-h4 ultra:text-h2 font-normal"
            >{{ logoutItem.label }}</span
          >
          <div v-html="logoutItem.icon" class="w-5 h-5 pointer-events-none"></div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRightPanelStore } from "@/stores/layoutStore";
import { onMounted, ref, watch, nextTick } from "vue";
import { closeSvg } from "@/utils/commonSvg";
import { getRightPanelMenuItems } from "@/utils/sidebarMenu";
import { useUserStore } from "@/stores/userStore";
import { useRouter } from "vue-router";
import { onErrorImage } from "@/utils/common";
import { useI18n } from "vue-i18n";

// Store and router setup
const rightPanelStore = useRightPanelStore();
const userStore = useUserStore();
const router = useRouter();
const { t } = useI18n();

// Extract menu items
const rightPanelMenuItems = getRightPanelMenuItems();
const mainRightPanelMenuItems = rightPanelMenuItems.slice(0, 4);
const logoutItem = rightPanelMenuItems[4];

const closeButton = ref(null);
const firstNavButton = ref(null);
const logoutButton = ref(null);
const panelContent = ref(null);

// Handle logout
const handleLogout = async () => {
  await userStore.logout();
};

// Handle navigation and close panel
const handleNavigation = (path) => {
  if (path) {
    rightPanelStore.hideRightPanel();
    router.push(path);
  }
};

const handleTabFromClose = (event) => {
  if (!event.shiftKey) {
    event.preventDefault();
    firstNavButton.value?.[0]?.focus();
  }
};

const handleShiftTabFromFirst = (event) => {
  event.preventDefault();
  closeButton.value?.focus();
};

const handleTabFromLastNav = (event) => {
  event.preventDefault();
  logoutButton.value?.focus();
};

const handleTabFromLogout = (event) => {
  event.preventDefault();
  closeButton.value?.focus();
};

watch(() => rightPanelStore.isRightPanelVisible, async (isVisible) => {
  if (isVisible) {
    await nextTick();
    closeButton.value?.focus();
  }
});

// Load user profile image if available
onMounted(async () => {
  if (!userStore.profileImage && userStore.loginResponse) {
    try {
      await userStore.getProfileImage();
    } catch (error) {
      console.error("Error loading profile image:", error);
    }
  }
});
</script>
